import React from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Box,
  LinearProgress,
  useTheme,
  Popover,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Logout,
  Settings,
  Brightness4,
  Brightness7,
  VpnKey,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { toggleSidebar, setTheme } from '../../store/slices/uiSlice'
import { logout } from '../../store/slices/authSlice'
import { GlobalSearch } from '../core/Navigation/GlobalSearch'
import { LanguageSwitcher } from './LanguageSwitcher'
import { useNavigate } from 'react-router-dom'
import logo from '../../assets/images/Company_logo.png'
import { ChangePasswordModal } from '../auth/ChangePasswordModal'

export const Header: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const muiTheme = useTheme()
  const isRtl = muiTheme.direction === 'rtl'
  const { user } = useAppSelector((state) => state.auth)
  const { notifications, loading, theme } = useAppSelector((state) => state.ui)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [notifAnchorEl, setNotifAnchorEl] = React.useState<null | HTMLElement>(null)
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false)
  const navigate = useNavigate()

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    dispatch(logout())
    handleMenuClose()
  }

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar())
  }

  const handleToggleTheme = () => {
    dispatch(setTheme(theme === 'light' ? 'dark' : 'light'))
  }

  const unreadCount = notifications.length;
  const themeModeText = theme === 'light' ? t('common.dark') : t('common.light')

  return (
    <AppBar
      position="fixed"
      component="header"
      role="banner"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#0F172A' : theme.palette.primary.main,
        borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#334155' : 'transparent'}`,
        boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 1px 4px 0 rgba(0, 0, 0, 0.4)' : undefined,
      }}
    >
      <Toolbar sx={{ direction: isRtl ? 'rtl' : 'ltr' }}>
        <IconButton
          color="inherit"
          aria-label={t('navigation.toggleMenu') || 'Toggle navigation menu'}
          onClick={handleToggleSidebar}
          edge="start"
          sx={{ 
            ...(isRtl ? { ml: 2 } : { mr: 2 })
          }}
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src={logo}
            alt="Al Maysarah AI"
            sx={{
              height: 48,
              width: 'auto',
              objectFit: 'contain',
              imageRendering: '-webkit-optimize-contrast',
              filter: 'contrast(1.1) brightness(1.05)',
            }}
          />
          <Typography variant="h6" noWrap component="div" sx={{ 
            flexGrow: 0, 
            ...(isRtl ? { ml: 0 } : { mr: 3 })
          }}>
            {t('appTitle') || 'Al Maysarah IM'}
          </Typography>
        </Box>

        {/* Global Search */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}>
          <GlobalSearch />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            color="inherit"
            onClick={handleToggleTheme}
            aria-label={`${t('common.switchTo')} ${themeModeText} ${t('common.mode')}`}
            title={`${t('common.switchTo')} ${themeModeText} ${t('common.mode')}`}
          >
            {theme === 'light' ? <Brightness4 /> : <Brightness7 />}
          </IconButton>

          {/* Language Switcher */}
          <LanguageSwitcher />

          <IconButton
            color="inherit"
            aria-label={`${unreadCount} ${t('common.unreadNotifications')}`}
            onClick={(e) => setNotifAnchorEl(e.currentTarget)}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Popover
            open={Boolean(notifAnchorEl)}
            anchorEl={notifAnchorEl}
            onClose={() => setNotifAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
            PaperProps={{
              sx: {
                width: 320,
                maxHeight: 400,
                borderRadius: 2.5,
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#334155' : 'rgba(0,0,0,0.1)'}`,
                boxShadow: '0 12px 28px -4px rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('common.notifications', 'Notifications')}
              </Typography>
            </Box>
            {notifications.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('common.noNotifications', 'No new notifications')}
                </Typography>
              </Box>
            ) : (
              <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                {notifications.map((notif: any, index: number) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={notif.title || notif.message}
                      secondary={notif.timestamp ? new Date(notif.timestamp).toLocaleString() : undefined}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Popover>

          <IconButton
            size="large"
            edge="end"
            aria-label={`${t('navigation.accountMenu')} ${user?.firstName} ${user?.lastName}`}
            aria-controls="account-menu"
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            onClick={handleMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
          </IconButton>

          <Menu
            id="account-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'account-menu',
              role: 'menu',
            }}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                borderRadius: 2.5,
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#334155' : 'rgba(0,0,0,0.1)'}`,
                boxShadow: '0 12px 28px -4px rgba(0, 0, 0, 0.5)',
                mt: 1.5,
                minWidth: 190,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ...(isRtl ? { mr: -0.5, ml: 1 } : { ml: -0.5, mr: 1 }),
                },
              },
            }}
            transformOrigin={{ horizontal: isRtl ? 'left' : 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: isRtl ? 'left' : 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }} role="menuitem">
              <AccountCircle sx={{ ...(isRtl ? { ml: 1 } : { mr: 1 }) }} aria-hidden="true" />
              {t('navigation.profile')}
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }} role="menuitem">
              <Settings sx={{ ...(isRtl ? { ml: 1 } : { mr: 1 }) }} aria-hidden="true" />
              {t('navigation.settings')}
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); setChangePasswordOpen(true); }} role="menuitem">
              <VpnKey sx={{ ...(isRtl ? { ml: 1 } : { mr: 1 }) }} aria-hidden="true" />
              {t('settings.changePassword', 'Change Password')}
            </MenuItem>
            <MenuItem onClick={handleLogout} role="menuitem">
              <Logout sx={{ ...(isRtl ? { ml: 1 } : { mr: 1 }) }} aria-hidden="true" />
              {t('navigation.logout')}
            </MenuItem>
          </Menu>

          {/* Change Password Modal */}
          <ChangePasswordModal
            open={changePasswordOpen}
            onClose={() => setChangePasswordOpen(false)}
          />
        </Box>
      </Toolbar>

      {/* Linear progress indicator for global loading state */}
      {loading && (
        <LinearProgress
          role="progressbar"
          aria-label={t('common.loading')}
          aria-busy="true"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#fff',
            },
          }}
        />
      )}
    </AppBar>
  )
}
